export interface SafeUrlOptions {
    allowMailto: boolean;
    allowData: boolean;
}

export const SecurityUtils = {
    // Encode untrusted text before injecting into HTML strings
    escapeHtml: (value: unknown): string => {
        if (value === null || value === undefined) return '';
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    },

    // Validate outbound URLs used in links/images
    isSafeUrl: (rawUrl: unknown, options: SafeUrlOptions = { allowMailto: true, allowData: false }): boolean => {
        if (!rawUrl || typeof rawUrl !== 'string') return false;
        const trimmed = rawUrl.trim();
        if (!trimmed) return false;

        // Explicit deny-list first
        if (/^(javascript|vbscript|file|blob):/i.test(trimmed)) return false;
        if (!options.allowData && /^data:/i.test(trimmed)) return false;

        try {
            const parsed = new URL(trimmed, window.location.origin);
            const protocol = (parsed.protocol || '').toLowerCase();
            if (protocol === 'http:' || protocol === 'https:') return true;
            if (options.allowMailto && protocol === 'mailto:') return true;
            if (options.allowData && protocol === 'data:') return true;
            return false;
        } catch {
            return false;
        }
    },

    // Safe href/src value for HTML attributes
    sanitizeUrl: (rawUrl: unknown, options: SafeUrlOptions = { allowMailto: true, allowData: false }): string => {
        if (!SecurityUtils.isSafeUrl(rawUrl, options)) return '';
        return SecurityUtils.escapeHtml(String(rawUrl).trim());
    },

    // Restrict image sources to http(s) and non-executable data URIs
    isSafeImageUrl: (rawUrl: unknown): boolean => {
        if (!rawUrl || typeof rawUrl !== 'string') return false;
        const trimmed = rawUrl.trim();
        if (/^https?:/i.test(trimmed)) return true;
        return /^data:image\/(png|jpeg|jpg|gif|webp);base64,/i.test(trimmed);
    },

    // Sanitize HTML to prevent XSS attacks
    sanitizeHtml: (html: unknown): string => {
        if (!html || typeof html !== 'string') return '';
        // Create a temporary element to parse HTML
        const temp = document.createElement('div');
        temp.textContent = html; // This escapes all HTML
        return temp.innerHTML;
    },

    // Sanitize HTML but allow safe formatting tags
    sanitizeRichText: (html: unknown): string => {
        if (!html || typeof html !== 'string') return '';
        const allowedTags = new Set(['b', 'i', 'u', 'strong', 'em', 'p', 'br', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'blockquote', 'code', 'pre', 'a', 'sub', 'sup']);
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
        const root = doc.body.firstElementChild;
        if (!root) return '';

        const sanitizeNode = (node: Element): void => {
            if (!node || node.nodeType !== Node.ELEMENT_NODE) return;
            const tag = node.tagName.toLowerCase();

            if (!allowedTags.has(tag)) {
                const parent = node.parentNode;
                if (!parent) return;
                while (node.firstChild) parent.insertBefore(node.firstChild, node);
                parent.removeChild(node);
                return;
            }

            Array.from(node.attributes).forEach(attr => {
                const name = attr.name.toLowerCase();
                if (name.startsWith('on') || name === 'style' || name === 'srcdoc') {
                    node.removeAttribute(attr.name);
                    return;
                }
                if (tag === 'a' && name === 'href') {
                    const safeHref = SecurityUtils.sanitizeUrl(attr.value, { allowMailto: true, allowData: false });
                    if (!safeHref) {
                        node.removeAttribute('href');
                    } else {
                        node.setAttribute('href', safeHref);
                        node.setAttribute('rel', 'noopener noreferrer');
                        node.setAttribute('target', '_blank');
                    }
                    return;
                }
                if (!(tag === 'a' && name === 'href')) {
                    node.removeAttribute(attr.name);
                }
            });

            Array.from(node.children).forEach(sanitizeNode);
        };

        Array.from(root.children).forEach(sanitizeNode);
        return root.innerHTML;
    },

    // Validate API key format (basic validation)
    isValidApiKey: (key: unknown): boolean => {
        if (!key || typeof key !== 'string') return false;
        // OpenRouter keys can be sk-or-v1-xxx or sk-or-v1:xxx format, 20+ chars
        // Allow alphanumeric, hyphens, underscores, and colons
        return key.length >= 20 && /^[a-zA-Z0-9\-_:]+$/.test(key);
    },

    // Validate email format
    isValidEmail: (email: unknown): boolean => {
        if (!email || typeof email !== 'string') return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },

    // Validate phone number (Australian format)
    isValidPhone: (phone: unknown): boolean => {
        if (!phone || typeof phone !== 'string') return true; // Optional field
        const cleaned = phone.replace(/[\s\-\(\)]/g, '');
        return /^(\+?61|0)?[2-9]\d{8}$/.test(cleaned);
    },

    // Rate limiting helper
    rateLimiter: (() => {
        const requests = new Map<string, number[]>();
        const WINDOW_MS = 60000; // 1 minute
        const MAX_REQUESTS = 30; // Max 30 requests per minute

        return {
            canMakeRequest: (key: string = 'default'): boolean => {
                const now = Date.now();
                const windowStart = now - WINDOW_MS;

                // Get existing requests for this key
                let keyRequests = requests.get(key) || [];
                // Filter to only requests in current window
                keyRequests = keyRequests.filter(t => t > windowStart);

                if (keyRequests.length >= MAX_REQUESTS) {
                    return false;
                }

                // Add this request
                keyRequests.push(now);
                requests.set(key, keyRequests);
                return true;
            },

            getRemainingRequests: (key: string = 'default'): number => {
                const now = Date.now();
                const windowStart = now - WINDOW_MS;
                const keyRequests = (requests.get(key) || []).filter(t => t > windowStart);
                return Math.max(0, MAX_REQUESTS - keyRequests.length);
            }
        };
    })()
};
